// SAD-1000: the Delivery Health charts' data, worded from the summary the
// page already read (no request of their own). Every mark carries the link
// to its rows: the Command Center narrowed to them (listHref), so the global
// filters always travel with a drill.
import { FILED_STATES, NO_OWNER, OWED_STATES } from './filters';
import { listHref } from './readings';
const MONTH_SHORT = {
    month: 'short',
    timeZone: 'UTC'
};
const MONTH_LONG = {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
};
const DAY_SHORT = {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
};
const DAY_LONG = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
};
const utc = (iso)=>new Date(`${iso}T00:00:00Z`);
const iso = (d)=>d.toISOString().slice(0, 10);
const fmt = (d, o)=>d.toLocaleDateString('en-US', o);
const addDays = (d, n)=>new Date(d.getTime() + n * 86400000);
const n = (v)=>v.toLocaleString();
/** The first and last day of a "YYYY-MM" month. */ function monthBounds(month) {
    const first = utc(`${month}-01`);
    const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0));
    return {
        first: iso(first),
        last: iso(last)
    };
}
/** A drill keeps every global filter but the ones the mark sets. */ const drill = (filter, patch)=>listHref({
        ...filter,
        due: '',
        from: '',
        to: '',
        ...patch
    });
// ---------------------------------------------------------------------------
// On-time rate by month, and late at month end under it.
// ---------------------------------------------------------------------------
/** One point per trend month: the on-time reading as the server gave it. */ export function trendPoints(s, filter) {
    return (s?.trend ?? []).map((m)=>{
        const r = m.reading;
        const { first, last } = monthBounds(m.month);
        return {
            id: m.month,
            label: fmt(utc(first), MONTH_SHORT),
            title: `Due in ${fmt(utc(first), MONTH_LONG)}`,
            value: r?.hasValue ? r.value : null,
            detail: r?.hasValue ? `${n(r.numerator)} of ${n(r.denominator)} approved on time. Click to list them.` : 'Nothing came due that month.',
            // The trend counts every date due that month, so its rows are every date.
            href: drill(filter, {
                period: 'all',
                states: [],
                from: first,
                to: last
            })
        };
    });
}
/**
 * One column per trend month: the dates late on its last day (today, for
 * this month). It opens the ones still late of those due before that day.
 */ export function lateColumns(s, filter) {
    const trend = s?.trend ?? [];
    const asOf = s?.asOf ?? '';
    return trend.map((m, i)=>{
        const { first, last } = monthBounds(m.month);
        const current = i === trend.length - 1;
        const end = current && asOf ? asOf : last;
        const dayBefore = iso(addDays(utc(end), -1));
        return {
            id: m.month,
            value: m.lateAtMonthEnd,
            tone: current && m.lateAtMonthEnd > 0 ? 'bad' : 'pending',
            labelled: current,
            title: current ? `Late today, ${fmt(utc(end), DAY_SHORT)}` : `Late at the end of ${fmt(utc(first), MONTH_LONG)}`,
            detail: current ? 'Click to list them.' : 'Click to list the ones still late.',
            href: drill(filter, {
                period: 'all',
                states: [
                    'late'
                ],
                to: dayBefore
            })
        };
    });
}
export const STATUS_SEGMENTS = [
    {
        key: 'filed',
        label: 'Filed',
        tone: 'good'
    },
    {
        key: 'at_risk',
        label: 'At risk',
        tone: 'warn'
    },
    {
        key: 'late',
        label: 'Late',
        tone: 'bad'
    },
    {
        key: 'not_yet_due',
        label: 'Not yet due',
        tone: 'pending'
    }
];
/** The status filter each segment drills into. */ const SEGMENT_STATES = {
    filed: FILED_STATES,
    at_risk: [
        'at_risk'
    ],
    late: [
        'late'
    ],
    not_yet_due: [
        'not_yet_due'
    ]
};
/** The status filter values each segment holds, to fade the ones a filter leaves out. */ const SEGMENT_HOLDS = {
    filed: FILED_STATES,
    at_risk: [
        'at_risk',
        'due_soon'
    ],
    late: [
        'late'
    ],
    not_yet_due: [
        'not_yet_due',
        'due_soon',
        'upcoming'
    ]
};
/** A scope's dates split as the bars draw them; the four parts partition its owed and filed dates. */ export function segmentsOf(c) {
    const sc = c?.stateCounts ?? {};
    const atRisk = c?.atRisk ?? 0;
    return {
        filed: (sc.approved ?? 0) + (sc.awaiting_review ?? 0) + (sc.rejected ?? 0),
        at_risk: atRisk,
        late: sc.late ?? 0,
        not_yet_due: (sc.due_soon ?? 0) - atRisk + (sc.upcoming ?? 0)
    };
}
function groupsOf(s, by) {
    if (!s) return [];
    if (by === 'program') {
        return s.programs.map((p)=>({
                id: p.programId,
                label: p.name || 'Untitled program',
                sublabel: p.ownerName || undefined,
                counts: p.counts,
                narrow: {
                    programs: [
                        p.programId
                    ],
                    teams: []
                }
            }));
    }
    if (by === 'owner') {
        return s.owners.map((o)=>({
                id: o.ownerContactId,
                label: o.ownerContactId === NO_OWNER ? 'No owner' : o.name || 'Unnamed contact',
                counts: o.counts,
                narrow: {
                    owners: [
                        o.ownerContactId
                    ]
                }
            }));
    }
    return s.workstreams.map((w)=>({
            id: w.workstreamId ? w.workstreamId : `contract:${w.programId}`,
            label: w.workstreamId ? w.name || 'Unnamed team' : 'Contract-wide, no team',
            sublabel: w.programName || undefined,
            counts: w.counts,
            // A program's contract-wide dates have no team to filter by: their
            // drill is the program.
            narrow: w.workstreamId ? {
                teams: [
                    w.workstreamId
                ]
            } : {
                programs: [
                    w.programId
                ],
                teams: []
            }
        }));
}
/**
 * The status chart's bars, worst first: by share late or at risk, then by
 * volume. With a status filter on, only the groups holding such dates are
 * shown, and the segments outside it are faded.
 */ export function statusRows(s, by, filter) {
    const wanted = new Set(filter.states);
    const faded = wanted.size === 0 ? [] : STATUS_SEGMENTS.filter((seg)=>!SEGMENT_HOLDS[seg.key].some((st)=>wanted.has(st))).map((seg)=>seg.key);
    const all = groupsOf(s, by).filter((g)=>wanted.size === 0 || (g.counts?.total ?? 0) > 0).map((g)=>{
        const values = segmentsOf(g.counts);
        const total = Object.values(values).reduce((a, b)=>a + b, 0);
        const bad = values.late + values.at_risk;
        const share = total > 0 ? Math.round(bad / total * 100) : 0;
        return {
            total,
            bad,
            share,
            row: {
                id: g.id,
                label: g.label,
                sublabel: g.sublabel,
                values,
                hrefs: Object.fromEntries(STATUS_SEGMENTS.map((seg)=>[
                        seg.key,
                        drill(filter, {
                            ...g.narrow,
                            states: SEGMENT_STATES[seg.key]
                        })
                    ])),
                faded,
                value: `${share}%`,
                valueTone: values.late > 0 ? 'danger' : values.at_risk > 0 ? 'warning' : 'muted',
                hint: 'Click to list them.'
            }
        };
    }).filter((g)=>g.total > 0).sort((a, b)=>b.share - a.share || b.bad - a.bad || b.total - a.total || a.row.label.localeCompare(b.row.label));
    return {
        rows: all.filter((g)=>g.bad > 0).map((g)=>g.row),
        quiet: all.filter((g)=>g.bad === 0).map((g)=>g.row)
    };
}
/** The noun of a group, plural, for "+ 9 teams with nothing late or at risk". */ export const GROUP_NOUN = {
    team: [
        'team',
        'teams'
    ],
    program: [
        'program',
        'programs'
    ],
    owner: [
        'owner',
        'owners'
    ]
};
// ---------------------------------------------------------------------------
// The due-date calendar.
// ---------------------------------------------------------------------------
const WEEKDAYS = [
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat',
    'Sun'
];
/**
 * Six weeks of days from the Monday of this week, working days only unless
 * something falls due on a weekend. A day before today counts what is late
 * or still owed on it; a day's link opens those dates.
 */ export function calendarView(s, filter) {
    const days = s?.calendarDays ?? [];
    const asOf = s?.asOf ?? '';
    const weekend = days.some((d, i)=>i % 7 >= 5 && d.count > 0);
    const width = weekend ? 7 : 5;
    const states = filter.states.length > 0 ? filter.states : OWED_STATES;
    const weeks = [];
    let busiest;
    for(let w = 0; w * 7 < days.length; w++){
        const week = days.slice(w * 7, w * 7 + 7);
        const monday = week[0]?.date ?? '';
        const label = monday ? fmt(utc(monday), DAY_SHORT) : '';
        weeks.push({
            id: monday,
            label,
            days: week.slice(0, width).map((d)=>{
                const parts = [
                    d.late > 0 ? `${n(d.late)} late` : '',
                    d.atRisk > 0 ? `${n(d.atRisk)} at risk` : ''
                ].filter(Boolean);
                return {
                    date: d.date,
                    label: fmt(utc(d.date), DAY_LONG),
                    count: d.count,
                    flag: d.late > 0 ? 'bad' : d.atRisk > 0 ? 'warn' : undefined,
                    past: !!asOf && d.date < asOf,
                    today: d.date === asOf,
                    detail: [
                        ...parts,
                        d.count > 0 ? 'Click to list them.' : ''
                    ].filter(Boolean).join(' · '),
                    // The calendar counts every date, whatever the period.
                    href: drill(filter, {
                        period: 'all',
                        states,
                        from: d.date,
                        to: d.date
                    })
                };
            })
        });
        const owed = week.reduce((a, d)=>a + d.count, 0);
        if (owed > 0 && (!busiest || owed > busiest.count)) busiest = {
            label,
            count: owed
        };
    }
    return {
        weeks,
        weekdays: WEEKDAYS.slice(0, width),
        busiest
    };
}
